import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Star, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";

const GLOSSARY = {
  SEO: "Search Engine Optimization",
  SEM: "Search Engine Marketing",
  SMM: "Social Media Marketing",
  PPC: "Pay-Per-Click Marketing",
  PRM: "Press Release Marketing",
  GM: "Guerrilla Marketing",
  VVM: "Viral Video Marketing",
};

const LEVELS = [
  { level: "Level 1", price: "$199", codes: ["SEO", "SEM"], adds: ["SEO", "SEM"], highlight: false },
  { level: "Level 2", price: "$299", codes: ["SEO", "SEM", "SMM"], adds: ["SMM"], highlight: false },
  { level: "Level 3", price: "$599", codes: ["SEO", "SEM", "SMM", "PPC"], adds: ["PPC"], highlight: true },
  { level: "Level 4", price: "$1,999", codes: ["SEO", "SEM", "SMM", "PPC", "PRM", "GM"], adds: ["PRM", "GM"], highlight: false },
  { level: "Level 5", price: "$2,999", codes: ["SEO", "SEM", "SMM", "PPC", "PRM", "GM", "VVM"], adds: ["VVM"], highlight: false },
];

export default function MonthlyMarketing() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Monthly Marketing Plans — SEO, SEM, Social & PPC Sacramento | Emergent Sacramento";
  }, []);

  return (
    <div className="bg-ink text-white font-body min-h-screen">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-36 pb-16 border-b border-line">
          <div className="container-x">
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mono-label text-accent">
              [ Monthly Marketing ]
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-head font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-5 max-w-4xl text-balance leading-[0.95]"
            >
              Marketing on retainer. Growth on autopilot.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-lg text-text-2 leading-relaxed max-w-2xl"
            >
              Pick a level — every tier stacks on the one before it. Cancel or upgrade anytime. Built
              for Sacramento businesses that want results every single month.
            </motion.p>
          </div>
        </section>

        {/* Tiers */}
        <section className="relative py-20">
          <div className="container-x">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {LEVELS.map((lvl, i) => (
                <motion.div
                  key={lvl.level}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                  data-testid={`mm-card-${i + 1}`}
                  className={`relative p-8 border flex flex-col ${
                    lvl.highlight ? "bg-surface-2 border-accent" : "bg-surface border-line"
                  }`}
                >
                  {lvl.highlight && (
                    <div className="absolute -top-3 left-8 bg-accent text-ink mono-label px-3 py-1 flex items-center gap-1">
                      <Star className="w-3 h-3 fill-ink" /> Most Popular
                    </div>
                  )}

                  <span className="mono-label text-text-2">{lvl.level}</span>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {lvl.codes.map((c) => (
                      <span
                        key={c}
                        title={GLOSSARY[c]}
                        className={`mono-label px-2 py-1 border !text-[0.6rem] ${
                          lvl.adds.includes(c) ? "border-accent text-accent bg-accent/10" : "border-line text-text-2"
                        }`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 flex items-end gap-1.5">
                    <span className="font-head font-black text-5xl">{lvl.price}</span>
                    <span className="text-text-2 mb-1.5 text-sm">/month</span>
                  </div>

                  <p className="text-text-2 text-sm mt-3">
                    {i === 0 ? "Everything you need to get found." : (
                      <>Everything in {LEVELS[i - 1].level}, plus <span className="text-white">{lvl.adds.join(" & ")}</span>.</>
                    )}
                  </p>

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {lvl.codes.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                        <span className="text-white/85">
                          <span className="font-semibold">{c}</span>
                          <span className="text-text-2"> — {GLOSSARY[c]}</span>
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="/#contact"
                    data-testid={`mm-cta-${i + 1}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 font-head font-bold py-3.5 transition-colors ${
                      lvl.highlight ? "bg-accent text-ink hover:bg-white" : "border border-line text-white hover:border-accent hover:text-accent"
                    }`}
                  >
                    Get started <ArrowUpRight className="w-4 h-4" />
                  </a>
                </motion.div>
              ))}
            </div>

            <p className="text-text-2 text-sm mt-10 text-center max-w-2xl mx-auto">
              Need something custom or a mix of services? We tailor monthly plans to your goals and
              budget — just reach out for a free strategy call.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
